import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, Stack, TextField, Typography } from "@mui/material";
import { updatePassword } from "firebase/auth";
import { auth } from "../../firebase/firebase";
import linguistApi from "services/linguistApi";
import { usePageTitle } from "hooks/usePageTitle";

// Map raw Firebase codes to human copy (portal convention, 2026-10-16, C-14).
const getInvitationSetupErrorMessage = (firebaseError) => {
  switch (firebaseError?.code) {
    case "auth/requires-recent-login":
      return "Please sign in again, then set your password.";
    case "auth/weak-password":
      return "Please choose a stronger password with at least 12 characters.";
    case "auth/network-request-failed":
      return "Couldn't reach the server — check your connection and try again.";
    default:
      return "Unable to update your password. Please try again.";
  }
};

const LinguistInvitationSetup = () => {
  usePageTitle("Complete your setup");

  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const savePassword = async () => {
    if (!auth.currentUser) {
      setError("You need to sign in first.");
      return;
    }

    if (password.length < 12) {
      setError("Please choose a password with at least 12 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await updatePassword(auth.currentUser, password);
      // Fire-and-forget password-changed notification (non-blocking).
      linguistApi.post("/notify-password-changed").catch(() => {});
      setMessage("Your password has been updated.");
      // Keep the busy state on and hold the page briefly so the confirmation
      // is actually readable before redirecting (2026-10-16, C-13).
      setTimeout(() => {
        navigate("/linguist-portal", { replace: true });
      }, 800);
    } catch (requestError) {
      setError(getInvitationSetupErrorMessage(requestError));
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight={800}>Complete your setup</Typography>
              <Typography variant="body2" color="text.secondary">
                Set a new password for your portal account.
              </Typography>
            </Box>

            {message ? <Alert severity="success">{message}</Alert> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}

            <TextField label="New password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} fullWidth />
            <TextField label="Confirm password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} fullWidth />
            <Button variant="contained" onClick={savePassword} disabled={saving}>
              {saving ? <CircularProgress size={22} color="inherit" /> : "Save password"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default LinguistInvitationSetup;